/**
 * Выбор одного значения. Для длинных справочников — с поиском внутри.
 */

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "./cn";
import { OverflowMarquee } from "./OverflowMarquee";
import { SHEET_PANEL, SHEET_ROW, useSheet } from "./sheet";
import type { PickerOption } from "./MultiSelectPicker";

interface SelectProps {
  options: PickerOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  invalid = false,
  id,
}: SelectProps) {
  // На телефоне список — нижний лист (sheet.ts).
  const sheet = useSheet();
  const current = options.find((option) => option.id === value) ?? null;
  // Архивный элемент остаётся видимым, если уже выбран (§7.1).
  const visible = options.filter((option) => option.archived !== true || option.id === value);

  return (
    // value={null} допустим: пока ничего не выбрано, кнопка показывает placeholder.
    <Listbox<"div", string | null>
      value={value}
      onChange={(next) => {
        if (next !== null) onChange(next);
      }}
      disabled={disabled}
    >
      <ListboxButton
        id={id}
        aria-label={label}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-surface",
          "px-3 text-left text-sm outline-none transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-60",
          invalid ? "border-danger" : "border-line focus-visible:border-accent data-open:border-accent",
        )}
      >
        <span className={cn("truncate", current ? "text-fg" : "text-faint")}>
          {current?.label ?? placeholder}
          {current?.archived === true && (
            <span className="ml-1.5 text-faint italic">архивный</span>
          )}
        </span>
        <ChevronDown className="size-4 shrink-0 text-faint" aria-hidden />
      </ListboxButton>

      <ListboxOptions
        // Высота — через --anchor-max-height, как у мультивыбора: max-h-*
        // перебивается строковым стилем Headless UI.
        {...(sheet
          ? { portal: true, className: SHEET_PANEL }
          : {
              anchor: { to: "bottom start", gap: 4, padding: 8 } as const,
              className:
                "z-50 w-(--button-width) rounded-md border border-line bg-raised p-1 shadow-md [--anchor-max-height:16rem] focus:outline-none",
            })}
      >
        {visible.map((option) => (
          <ListboxOption
            key={option.id}
            value={option.id}
            disabled={option.disabled === true}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-sm text-fg",
              sheet ? SHEET_ROW : "px-2.5 py-1.5 text-[13px]",
              "data-focus:bg-sunken data-disabled:cursor-not-allowed data-disabled:opacity-40",
            )}
          >
            {({ selected }) => (
              <>
                {/* Вложенность дерева — отступом в списке, а не пробелами в подписи:
                    в кнопке выбранное значение стоит без отступа. */}
                {(option.depth ?? 0) > 0 && (
                  <span aria-hidden className="shrink-0" style={{ width: `${String((option.depth ?? 0) * 16)}px` }} />
                )}
                <Check
                  className={cn("size-4 shrink-0 text-accent", selected ? "opacity-100" : "opacity-0")}
                  aria-hidden
                />
                <OverflowMarquee>
                  {option.label}
                  {option.hint !== undefined && (
                    <span className="ml-1.5 text-faint">{option.hint}</span>
                  )}
                </OverflowMarquee>
              </>
            )}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}
